/*
 * Площадки Proxmox VE установки (бэкенд-плагин viva-proxmox, GET /sites): узлы, хранилища, сети, пулы, VM и
 * образы каждой площадки — для раздела «Proxmox». Только чтение: ресурсы создают и удаляют шаблоны раздела.
 */
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export type ProxmoxNode = { name: string; status: string; cpu: number; maxcpu: number; mem: number; maxmem: number };
export type ProxmoxStorage = { node: string; storage: string; type: string; content: string[]; used: number; total: number; status: string };
export type ProxmoxNetwork = { kind: 'vnet' | 'bridge'; name: string; zone?: string; node?: string; alias?: string; cidr?: string; gateway?: string; snat?: boolean };
export type ProxmoxPool = { id: string; comment: string; viva: boolean; members: number };
export type ProxmoxVm = {
  vmid: number;
  name: string;
  node: string;
  type: string;
  status: string;
  pool: string;
  tags: string[];
  cores: number;
  memoryGb: number;
  diskGb: number;
  ip?: string;
  viva: boolean;
};
export type ProxmoxImage = { node: string; storage: string; volid: string; name: string; content: string; sizeGb: number };
export type ProxmoxSite = {
  id: string;
  endpoint: string;
  version?: string;
  error?: string;
  nodes: ProxmoxNode[];
  storages: ProxmoxStorage[];
  networks: ProxmoxNetwork[];
  pools: ProxmoxPool[];
  vms: ProxmoxVm[];
  images: ProxmoxImage[];
};

export async function fetchProxmoxSites(apis: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }): Promise<ProxmoxSite[]> {
  const base = await apis.discoveryApi.getBaseUrl('viva-proxmox');
  const response = await apis.fetchApi.fetch(`${base}/sites`);
  if (!response.ok) {
    throw new Error(`Площадки Proxmox: HTTP ${response.status}`);
  }
  return (await response.json()).sites;
}

/**
 * Чем ресурс площадки управляется: ресурс раздела (шаблон proxmox-*: VM с тегом vm, пул viva/<имя> без кластера),
 * VM кластера ViVA (пул viva/<кластер>) или внешний (чужой пул, вне ViVA — только чтение).
 */
export const vmOwner = (vm: ProxmoxVm): 'resource' | 'cluster' | 'external' => {
  if (!vm.viva) return 'external';
  return vm.tags.includes('vm') ? 'resource' : 'cluster';
};
export const poolOwner = (pool: ProxmoxPool): 'resource' | 'cluster' | 'external' => {
  if (!pool.viva || pool.id === 'viva') return 'external';
  return pool.comment.startsWith('ViVA: кластер') ? 'cluster' : 'resource';
};

/** Ссылка на шаблон удаления ресурса раздела (proxmox-resource-delete) с заполненной формой. */
export const deleteResourceLink = (base: string, site: string, kind: 'vms' | 'pools' | 'images' | 'networks', name: string) =>
  `${base}?formData=${encodeURIComponent(JSON.stringify({ site, kind, name }))}`;
